/**
 * Slug helpers for per-user report templates.
 *
 * Section `kind` slugs are user-invisible stable identifiers that the BE uses
 * to key its routing map. New user-defined sections get a slug generated from
 * the label they type via `kebabify`.
 */

/**
 * Lower-case the string, replace whitespace runs with a single hyphen, strip
 * non-alphanumeric (non-hyphen) characters, collapse repeated hyphens, and
 * trim leading/trailing hyphens. Throws on empty or symbol-only input — the
 * BE rejects empty kinds, so we surface the problem at the input boundary
 * instead of waiting for a 422.
 *
 *   kebabify("Liver")              -> "liver"
 *   kebabify("Pelvic Organs")      -> "pelvic-organs"
 *   kebabify("Kidneys/Ureters")    -> "kidneys-ureters"
 *   kebabify("  Bones  ")          -> "bones"
 *   kebabify("INCIDENTAL FINDINGS")-> "incidental-findings"
 *   kebabify("???")                -> throws
 */
export function kebabify(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) {
    throw new Error("Cannot generate a slug from an empty label");
  }
  const slug = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!slug) {
    throw new Error(`Cannot generate a slug from ${JSON.stringify(label)}`);
  }
  // The BE expects a leading letter. Numeric-leading slugs are rare in
  // practice (labels start with words), so this throws rather than prefixes
  // — a silent prefix would diverge the user's chosen name from the slug.
  if (!/^[a-z]/.test(slug)) {
    throw new Error(`Slug must start with a letter (got ${JSON.stringify(slug)})`);
  }
  return slug;
}
